import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  ChartLineUp,
  Stack,
  Trophy,
  SignOut,
  List,
  X,
  UserCircle,
  Storefront,
  Crown,
  Users,
  Ranking,
  CalendarBlank,
  GearSix,
  Lightning,
  ChartBar,
  DiceFive,
  Spade,
  Wallet,
  SlidersHorizontal,
  Club
} from '@phosphor-icons/react';
import NotificationBell from '@/components/NotificationBell';
import LanguageToggle from '@/components/LanguageToggle';
import PosseAppSwitcher from '@/components/PosseAppSwitcher';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const navLinks = [
  { to: '/dashboard', label: 'DASHBOARD', icon: ChartLineUp },
  { to: '/builder', label: 'BUILDER', icon: Stack },
  { to: '/my-parlays', label: 'PARLAYS', icon: Trophy },
  { to: '/craps', label: 'CRAPS', icon: DiceFive },
  { to: '/poker', label: 'POKER', icon: Spade },
  { to: '/blackjack', label: '21', icon: Club },
  { to: '/live', label: 'LIVE', icon: Lightning },
  { to: '/stats', label: 'STATS', icon: ChartBar },
  { to: '/marketplace', label: 'MARKET', icon: Storefront },
  { to: '/leaderboard', label: 'RANKS', icon: Ranking },
  { to: '/champions', label: 'CROWNS', icon: Crown },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-50 glass border-b border-white/10" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={user ? '/dashboard' : '/'} className="flex items-center gap-2 group" data-testid="navbar-logo">
            <img
              src="/logo-mark.png"
              alt="Parlay Posse"
              width="32"
              height="32"
              className="h-8 w-8 group-hover:scale-110 transition-transform duration-150"
            />
            <span className="font-heading font-black text-xl tracking-tight uppercase text-white">
              PARLAY <span className="text-[#FF6B1A]">POSSE</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          {user && (
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map(({ to, label, icon: Icon }) => {
                const active = location.pathname === to;
                return (
                  <Link
                    key={to}
                    to={to}
                    data-testid={`nav-${to.replace('/', '')}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-heading font-bold uppercase tracking-wide transition-all duration-150 ${
                      active
                        ? 'bg-[#FF6B1A]/15 text-[#FF6B1A] border border-[#FF6B1A]/30'
                        : 'text-white/60 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <Icon size={18} weight={active ? 'fill' : 'regular'} />
                    {label}
                  </Link>
                );
              })}
            </div>
          )}

          {/* Right side */}
          <div className="flex items-center gap-3">
            {user && user.subscription !== 'pro' && (
              <Link
                to="/pricing"
                data-testid="nav-upgrade-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FF6B1A] text-black rounded-md text-xs font-heading font-black uppercase tracking-wide hover:bg-[#FF853E] transition-colors"
              >
                <Crown size={14} weight="fill" />
                UPGRADE
              </Link>
            )}
            {user && user.subscription === 'pro' && (
              <span className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-[#34C759]/10 border border-[#34C759]/30 rounded-md text-[#34C759] text-xs font-heading font-bold uppercase tracking-wide" data-testid="pro-badge">
                <Crown size={14} weight="fill" />
                PRO
              </span>
            )}
            {user && <NotificationBell />}
            <PosseAppSwitcher />
            <LanguageToggle className="hidden sm:inline-flex" />
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/5 border border-white/10 hover:bg-white/10 transition-all duration-150"
                    data-testid="user-menu-trigger"
                  >
                    <UserCircle size={22} weight="duotone" className="text-white/70" />
                    <span className="text-sm font-medium text-white/80 hidden sm:inline">{user.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#111115] border-white/10 min-w-[180px]">
                  <DropdownMenuItem asChild className="text-white/70 hover:text-white cursor-pointer focus:bg-white/5 focus:text-white">
                    <Link to="/wallet"><Wallet size={16} className="mr-2" />Wallet</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="text-white/70 hover:text-white cursor-pointer focus:bg-white/5 focus:text-white">
                    <Link to="/referrals"><Users size={16} className="mr-2" />Referrals</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="text-white/70 hover:text-white cursor-pointer focus:bg-white/5 focus:text-white">
                    <Link to="/pick-history"><CalendarBlank size={16} className="mr-2" />Pick History</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="text-white/70 hover:text-white cursor-pointer focus:bg-white/5 focus:text-white">
                    <Link to="/settings"><SlidersHorizontal size={16} className="mr-2" />Settings</Link>
                  </DropdownMenuItem>
                  {user.role === 'admin' && (
                    <DropdownMenuItem asChild className="text-[#FF6B1A] hover:text-[#FF853E] cursor-pointer focus:bg-white/5 focus:text-[#FF6B1A]">
                      <Link to="/admin"><GearSix size={16} className="mr-2" />Admin Panel</Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="text-white/70 hover:text-white cursor-pointer focus:bg-white/5 focus:text-white"
                    data-testid="logout-btn"
                  >
                    <SignOut size={16} className="mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/login"
                  data-testid="nav-login-btn"
                  className="px-4 py-2 text-sm font-heading font-bold uppercase tracking-wide text-white/70 hover:text-white transition-colors duration-150"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  data-testid="nav-register-btn"
                  className="px-4 py-2 text-sm font-heading font-bold uppercase tracking-wide bg-[#FF6B1A] text-white rounded-md hover:bg-[#FF853E] transition-colors duration-150"
                >
                  Sign Up
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            {user && (
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="md:hidden p-2 text-white/60 hover:text-white"
                data-testid="mobile-menu-toggle"
              >
                {mobileOpen ? <X size={24} /> : <List size={24} />}
              </button>
            )}
          </div>
        </div>

        {/* Mobile Nav */}
        {user && mobileOpen && (
          <div className="md:hidden pb-4 border-t border-white/10 pt-3 animate-fade-in" data-testid="mobile-nav-menu">
            {navLinks.map(({ to, label, icon: Icon }) => {
              const active = location.pathname === to;
              return (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-heading font-bold uppercase tracking-wide ${
                    active ? 'bg-[#FF6B1A]/15 text-[#FF6B1A]' : 'text-white/60 hover:text-white'
                  }`}
                >
                  <Icon size={20} weight={active ? 'fill' : 'regular'} />
                  {label}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </nav>
  );
}
